/**
 * Monegasy Payment SDK
 * @version 1.0.0
 * @description SDK JavaScript officiel pour intégrer les paiements Monegasy
 * @author Monegasy <support@monegasy.com>
 * @license MIT
 * @see https://docs.monegasy.com
 */

/** Version du SDK — doit rester synchro avec `package.json`. */
export const SDK_VERSION = "1.2.0";

/** URLs de production par défaut */
export const DEFAULT_API_URL = "https://api.monegasy.com";
export const DEFAULT_WEB_URL = "https://monegasy.com";

/** URLs de sandbox par défaut */
export const SANDBOX_API_URL = "http://localhost:3000";
export const SANDBOX_WEB_URL = "http://localhost:5173";

/** Deep Link Configuration */
export const DEEP_LINK_SCHEME = "monegasy";
export const APP_STORE_URL = "https://apps.apple.com/app/monegasy";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.monegasy.wallet";

// ===================== ERROR HIERARCHY =====================

/**
 * Erreur de base pour toutes les erreurs SDK Monegasy.
 * Permet aux intégrateurs de filtrer via `instanceof` :
 *
 * ```ts
 * try {
 *   await sdk.createPaymentLink(...);
 * } catch (err) {
 *   if (err instanceof MonegasyNetworkError) {
 *     // pas de connexion
 *   } else if (err instanceof MonegasyApiError && err.statusCode === 401) {
 *     // clé API invalide
 *   }
 * }
 * ```
 */
export class MonegasyError extends Error {
  /** Code court interne, ex: "INVALID_AMOUNT", "NETWORK". */
  public readonly code: string;

  constructor(message: string, code = "MONEGASY_ERROR") {
    super(message);
    this.name = "MonegasyError";
    this.code = code;
    // Préserve la stack trace en V8
    if (typeof (Error as any).captureStackTrace === "function") {
      (Error as any).captureStackTrace(this, this.constructor);
    }
  }
}

/** Validation locale : montant invalide, description vide, etc. */
export class MonegasyValidationError extends MonegasyError {
  constructor(message: string) {
    super(message, "VALIDATION");
    this.name = "MonegasyValidationError";
  }
}

/** Échec réseau : DNS, timeout, offline. */
export class MonegasyNetworkError extends MonegasyError {
  constructor(message: string) {
    super(message, "NETWORK");
    this.name = "MonegasyNetworkError";
  }
}

/** Réponse API non-2xx (avec statut HTTP). */
export class MonegasyApiError extends MonegasyError {
  public readonly statusCode: number;
  public readonly data: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message, "API");
    this.name = "MonegasyApiError";
    this.statusCode = statusCode;
    this.data = data;
  }
}

/**
 * Information sur le device de l'utilisateur
 */
export interface DeviceInfo {
  /** Est-ce un appareil mobile ? */
  isMobile: boolean;
  /** Est-ce un iPhone/iPad/iPod ? */
  isIOS: boolean;
  /** Est-ce un appareil Android ? */
  isAndroid: boolean;
  /** Est-ce une tablette ? */
  isTablet: boolean;
  /** Est-ce un ordinateur ? */
  isDesktop: boolean;
  /** URL du store approprié */
  storeUrl: string;
  /** Nom du store (App Store ou Google Play) */
  storeName: string;
  /** User Agent */
  userAgent: string;
}

/**
 * Configuration du SDK Monegasy
 */
export interface MonegasyConfig {
  /** Clé API du marchand (requis) */
  apiKey: string;
  /** URL de base de l'API (optionnel) */
  baseURL?: string;
  /** URL de la page web de paiement (optionnel) */
  webURL?: string;
  /** Mode sandbox/test (optionnel, défaut: false) */
  sandbox?: boolean;
  /** Timeout HTTP par requête en millisecondes (défaut: 30 000). */
  timeoutMs?: number;
  /** Nombre max de retries auto sur 429/5xx (défaut: 3, 0 = désactivé). */
  maxRetries?: number;
}

/**
 * Paramètres pour créer un lien de paiement
 */
/**
 * Mode de paiement étalé disponible.
 *  - `"3x"` : 3 échéances égales
 *  - `"4x"` : 4 échéances égales
 *  - `"deposit_balance"` : acompte + solde
 */
export type InstallmentMode = "3x" | "4x" | "deposit_balance";

/**
 * Options pour activer le paiement en plusieurs fois sur un lien.
 *
 * Si `enabled = true`, le client verra un picker à la confirmation
 * du paiement. Les modes proposés sont l'intersection de :
 *  1. `allowedModes` (passé ici) — si omis, tous les modes activés
 *     par le marchand sont disponibles
 *  2. La configuration globale du marchand (modes activés, bornes
 *     min/max amount)
 *
 * Si aucun mode n'est compatible (montant hors bornes, etc.), le
 * client ne voit que le paiement classique en une fois.
 */
export interface InstallmentOptions {
  /** Activer la facilité sur ce lien */
  enabled: boolean;
  /** Sous-ensemble des modes autorisés. Si omis, tous activés. */
  allowedModes?: InstallmentMode[];
  /** Override du pourcentage d'acompte (config marchand sinon) */
  depositPercentage?: number;
  /** Override du délai du solde en jours (config marchand sinon) */
  balanceDueDays?: number;
}

/**
 * Status d'un plan d'échéancier côté serveur.
 */
export type InstallmentPlanStatus =
  | "active"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * Status d'une échéance individuelle.
 */
export type InstallmentLineStatus =
  | "pending"
  | "due"
  | "retrying"
  | "paid"
  | "failed"
  | "cancelled";

/**
 * Preview d'une option de facilité de paiement (avant création du plan).
 */
export interface InstallmentPreview {
  type: InstallmentMode;
  installments: Array<{
    sequence: number;
    amount: number;
    feeAmount: number;
    dueDate: string;
    label: string;
  }>;
  totalAmount: number;
  totalFees: number;
  description: string;
}

/**
 * Configuration des facilités de paiement par marchand.
 */
export interface MerchantInstallmentConfig {
  id: string;
  merchantId: string;
  enabled3x: boolean;
  enabled4x: boolean;
  enabledDepositBalance: boolean;
  minAmount: number | null;
  maxAmount: number | null;
  intervalDays: number;
  depositPercentage: number | null;
  balanceDueDays: number | null;
  customerFeePercentage: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Échéance individuelle d'un plan.
 */
export interface InstallmentLineInfo {
  id: string;
  planId: string;
  sequence: number;
  amount: number;
  feeAmount: number;
  dueDate: string;
  status: InstallmentLineStatus;
  transactionId: string | null;
  paidAt: string | null;
  failedAttempts: number;
  nextRetryAt: string | null;
}

/**
 * Plan d'échéancier complet (vue marchand).
 */
export interface InstallmentPlanInfo {
  id: string;
  paymentLinkId: string | null;
  orderId: string | null;
  merchantId: string;
  buyerUserId: string;
  type: InstallmentMode;
  totalInstallments: number;
  totalAmount: number;
  collectedAmount: number;
  customerFeePercentage: number;
  status: InstallmentPlanStatus;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  installments?: InstallmentLineInfo[];
}

/**
 * Configuration webhook d'un marchand.
 */
export interface WebhookConfig {
  id: string;
  merchantId: string;
  url: string;
  events: string[];
  secret?: string;
  retryCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Statut possible d'une livraison webhook.
 */
export type WebhookLogStatus = "success" | "failed" | "retrying";

/**
 * Log d'une tentative de livraison webhook.
 */
export interface WebhookLog {
  id: string;
  webhookId: string;
  event: string;
  payload: Record<string, unknown>;
  attemptCount: number;
  status: WebhookLogStatus;
  responseStatus: number | null;
  responseBody: string | null;
  errorMessage: string | null;
  createdAt: string;
}

/**
 * Résultat du test d'un webhook avec un événement personnalisé.
 */
export interface WebhookTestResult {
  success: boolean;
  statusCode?: number;
  latencyMs: number;
  responseBody?: string;
  responseHeaders?: Record<string, string>;
  error?: string;
  sentPayload: Record<string, unknown>;
  targetUrl: string;
}

export interface CreatePaymentLinkParams {
  /** Montant en Ariary (requis, doit être > 0) */
  amount: number;
  /** Description du paiement (requis) */
  description: string;
  /** Nom du produit (optionnel) */
  productName?: string;
  /** URL de l'image du produit (optionnel) */
  productImage?: string;
  /** Métadonnées personnalisées (optionnel) */
  metadata?: Record<string, unknown>;
  /** URL de redirection après succès (optionnel) */
  successUrl?: string;
  /** URL de redirection après annulation (optionnel) */
  cancelUrl?: string;
  /** URL du webhook pour les notifications (optionnel) */
  webhookUrl?: string;
  /** Durée de validité en heures (optionnel, défaut: 24) */
  expiresInHours?: number;
  /**
   * Activer le paiement en plusieurs fois (3x, 4x, acompte+solde).
   *
   * Exemple :
   * ```ts
   * await monegasy.createPaymentLink({
   *   amount: 150000,
   *   description: "Smartphone Samsung",
   *   installments: {
   *     enabled: true,
   *     allowedModes: ["3x", "4x"],
   *   },
   * });
   * ```
   *
   * À la confirmation du paiement, le client choisit son mode dans
   * l'application Monegasy. Les premières échéances sont prélevées
   * immédiatement ; les suivantes le seront automatiquement aux dates
   * planifiées. Vous recevez un webhook `installment.paid` à chaque
   * échéance prélevée, et `plan.completed` quand toutes ont été
   * encaissées.
   */
  installments?: InstallmentOptions;
}

/**
 * Réponse d'un lien de paiement
 */
export interface PaymentLink {
  /** ID interne du paiement */
  id: string;
  /** ID public du lien de paiement */
  linkId: string;
  /** Montant en Ariary */
  amount: number;
  /** Devise (MGA) */
  currency: string;
  /** Description du paiement */
  description: string;
  /** Statut du paiement */
  status: PaymentStatus;
  /** URL de la page de paiement web */
  paymentUrl: string;
  /** Deep link pour l'app mobile */
  mobileDeepLink: string;
  /** Nom du produit */
  productName?: string;
  /** Date de création */
  createdAt?: string;
  /** Date d'expiration */
  expiresAt?: string;
  /** Permet des propriétés additionnelles */
  [key: string]: unknown;
}

/**
 * Statuts possibles d'un paiement
 */
export type PaymentStatus = "pending" | "paid" | "expired" | "cancelled";

// ──────────────────────────────────────────────────────────────────────
//  Types Plateforme (« Connect » / partenaire) — clés plateforme uniquement
// ──────────────────────────────────────────────────────────────────────

export interface CreatePlatformMerchantParams {
  /** Numéro du pro = son identité Monegasy (`0XXXXXXXXX` ou `+261XXXXXXXXX`). */
  phone: string;
  businessName: string;
  businessDescription?: string;
  businessAddress?: string;
  businessEmail?: string;
  /** Id de ce pro dans ton système (mapping retour via webhook). */
  externalRef?: string;
}

export interface PlatformMerchant {
  merchantId: string;
  userId: string;
  /** `provisioned` = compte créé ; `linked_existing` = compte Monegasy déjà présent. */
  status: "provisioned" | "linked_existing";
  /** true si le pro doit encore réclamer son compte (connexion-SMS) pour retirer. */
  claimRequired: boolean;
  businessName: string;
}

export interface PlatformSubMerchant {
  merchantId: string;
  businessName: string;
  slug: string | null;
  externalRef: string | null;
  isVerified: boolean;
  createdAt: string;
}

export interface CreatePlatformPaymentLinkParams
  extends CreatePaymentLinkParams {
  /** Sous-marchand bénéficiaire (doit appartenir à la plateforme). */
  merchantId: string;
}

// ──────────────────────────────────────────────────────────────────────
//  Types Produits (catalogue + recherche)
// ──────────────────────────────────────────────────────────────────────

export interface ProductCategory {
  id: string;
  name: string;
  icon?: string | null;
  color?: string | null;
}

/**
 * Produit dans le catalogue public. Forme allégée — pour le détail complet,
 * utilise l'endpoint `/products/:id/public` (cf. mobile scan QR).
 */
export interface CatalogProduct {
  id: string;
  name: string;
  price: number;
  /** Prix effectif si en promo (sinon === price). */
  effectivePrice: number;
  onSale: boolean;
  imageUrl: string | null;
  stock: number;
  merchantName: string;
  merchantId: string;
  merchantSlug: string | null;
  /** Distance en km si lat/lng fournis dans la recherche, sinon null. */
  distanceKm: number | null;
  category: ProductCategory | null;
}

export interface CatalogResponse {
  items: CatalogProduct[];
  total: number;
  page: number;
  pageSize: number;
}

export interface SearchProductsParams {
  /** Mot-clé recherché dans nom/description. */
  search?: string;
  /** ID d'une catégorie système (cf. `/product-categories`). */
  categoryId?: string;
  /** Géolocalisation : si fournie, les résultats sont triés par proximité. */
  lat?: number;
  lng?: number;
  /** Rayon de recherche en km. Combiner avec lat/lng. */
  radiusKm?: number;
  page?: number;
  pageSize?: number;
}

export interface StorefrontMerchant {
  id: string;
  userId: string;
  businessName: string;
  businessDescription: string | null;
  tagline: string | null;
  logo: string | null;
  coverImageUrl: string | null;
  slug: string;
  latitude: number | null;
  longitude: number | null;
  businessAddress: string | null;
  isVerified: boolean;
}

export interface StorefrontResponse {
  merchant: StorefrontMerchant;
  products: Array<{
    id: string;
    name: string;
    description?: string;
    price: number;
    discountPrice: number | null;
    imageUrl: string | null;
    stock: number;
    category: ProductCategory | null;
  }>;
  productsCount: number;
}

/**
 * Paramètres pour afficher un bouton de paiement
 */
export interface RenderButtonParams extends CreatePaymentLinkParams {
  /** Texte du bouton (optionnel) */
  buttonText?: string;
  /** Styles CSS personnalisés pour le bouton (optionnel) */
  buttonStyle?: Record<string, string>;
  /** Callback appelé en cas de succès */
  onSuccess?: (payment: PaymentLink) => void;
  /** Callback appelé en cas d'erreur */
  onError?: (error: Error) => void;
  /** Callback appelé en cas d'annulation */
  onCancel?: () => void;
}

// ===================== TYPES POUR LES ABONNEMENTS =====================

/**
 * Période de facturation pour les abonnements
 */
export type BillingPeriod = "monthly" | "quarterly" | "yearly";

/**
 * Statut d'un plan d'abonnement
 */
export type PlanStatus = "active" | "inactive" | "archived";

/**
 * Statut d'un abonnement utilisateur
 */
export type SubscriptionStatus =
  | "active"
  | "trial"
  | "paused"
  | "cancelled"
  | "expired"
  | "payment_failed";

/**
 * Paramètres pour créer un plan d'abonnement
 */
export interface CreateSubscriptionPlanParams {
  /** Nom du plan (requis) */
  name: string;
  /** Description du plan (optionnel) */
  description?: string;
  /** Prix en Ariary (requis, doit être > 0) */
  price: number;
  /** Période de facturation (requis) */
  billingPeriod: BillingPeriod;
  /** Nombre de jours d'essai gratuit (optionnel, défaut: 0) */
  trialDays?: number;
  /** Liste des fonctionnalités incluses (optionnel) */
  features?: string[];
}

/**
 * Paramètres pour mettre à jour un plan d'abonnement
 */
export interface UpdateSubscriptionPlanParams {
  /** Nom du plan (optionnel) */
  name?: string;
  /** Description du plan (optionnel) */
  description?: string;
  /** Prix en Ariary (optionnel) */
  price?: number;
  /** Période de facturation (optionnel) */
  billingPeriod?: BillingPeriod;
  /** Nombre de jours d'essai gratuit (optionnel) */
  trialDays?: number;
  /** Liste des fonctionnalités incluses (optionnel) */
  features?: string[];
  /** Statut du plan (optionnel) */
  status?: PlanStatus;
}

/**
 * Réponse d'un plan d'abonnement
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  price: number;
  billingPeriod: BillingPeriod;
  trialDays: number;
  features?: string[];
  status: PlanStatus;
  subscriberCount: number;
  qrCodeData?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Réponse d'un abonné
 */
export interface Subscriber {
  id: string;
  userId: string;
  userName?: string;
  userPhone?: string;
  plan: {
    id: string;
    name: string;
    price: number;
  };
  status: SubscriptionStatus;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndDate?: string;
  nextBillingDate: string;
  amount: number;
  createdAt: string;
}

/**
 * Statistiques des abonnements
 */
export interface SubscriptionStats {
  plans: {
    total: number;
    active: number;
  };
  subscribers: {
    total: number;
    active: number;
    byStatus: Record<string, number>;
  };
  revenue: {
    monthlyEstimate: number;
    currency: string;
  };
}

/**
 * Liens d'inscription à un plan
 */
export interface SubscribeLinks {
  plan: {
    id: string;
    name: string;
    price: number;
    billingPeriod: BillingPeriod;
    trialDays: number;
  };
  links: {
    webUrl: string;
    mobileDeepLink: string;
    qrCodeData?: string;
  };
}

// ===================== TYPES POUR LES WEBHOOKS =====================

/**
 * Événements webhook disponibles
 */
export type WebhookEventType =
  | "payment.completed"
  | "payment.failed"
  | "payment.cancelled"
  | "payment.refunded"
  | "payment.test";

/**
 * Données du webhook pour un paiement réussi
 */
export interface WebhookPaymentCompletedData {
  /** ID du lien de paiement (ex: "pl_abc123") */
  paymentLinkId: string;
  /** Montant en Ariary */
  amount: number;
  /** Devise (ex: "Ar") */
  currency: string;
  /** Statut du paiement */
  status: "completed";
  /** Description du paiement */
  description: string;
  /** Nom du produit (si fourni) */
  productName?: string;
  /** Téléphone du client */
  customerPhone?: string;
  /** Date du paiement */
  paidAt: string;
  /** ID de la transaction Monegasy */
  transactionId?: string;
  /** Métadonnées personnalisées */
  metadata?: string;
  /** URL de succès (si fournie) */
  successUrl?: string;
}

/**
 * Données du webhook pour un paiement échoué
 */
export interface WebhookPaymentFailedData {
  paymentLinkId: string;
  amount: number;
  currency: string;
  status: "failed";
  description: string;
  error: string;
  metadata?: string;
}

/**
 * Données du webhook pour un paiement annulé
 */
export interface WebhookPaymentCancelledData {
  paymentLinkId: string;
  amount: number;
  currency: string;
  status: "cancelled";
  description: string;
  metadata?: string;
}

/**
 * Données du webhook pour un remboursement
 */
export interface WebhookPaymentRefundedData {
  refundId: string;
  originalTransactionId: string;
  amount: number;
  currency: string;
  status: "refunded";
  reason?: string;
}

/**
 * Payload complet d'un webhook Monegasy
 */
export interface WebhookPayload<T = Record<string, unknown>> {
  /** Type d'événement */
  event: WebhookEventType;
  /** Données de l'événement */
  data: T;
  /** Horodatage ISO */
  timestamp: string;
  /** ID du webhook */
  webhook_id: string;
}

/**
 * Headers envoyés avec chaque webhook
 */
export interface WebhookHeaders {
  /** Signature HMAC SHA256 du payload */
  "x-monegasy-signature": string;
  /** Type d'événement */
  "x-monegasy-event": string;
  "content-type": "application/json";
}

/**
 * Options pour la popup de paiement
 */
export interface PopupOptions {
  /** Largeur de la popup (défaut: 500) */
  width?: number;
  /** Hauteur de la popup (défaut: 700) */
  height?: number;
}

/**
 * SDK Monegasy pour l'intégration des paiements
 * @example
 * ```typescript
 * const sdk = new MonegasySDK({
 *   apiKey: 'mk_live_xxxxxxx',
 *   sandbox: false
 * });
 *
 * await sdk.renderPaymentButton({
 *   amount: 50000,
 *   description: 'Achat produit'
 * }, '#payment-button');
 * ```
 */
export default class MonegasySDK {
  private readonly apiKey: string;
  private readonly baseURL: string;
  private readonly webURL: string;
  private readonly isSandbox: boolean;
  /** Type détecté depuis le préfixe de la clé. Permet au SDK de
   *  throw early sur les routes qui exigent une secret key. */
  private readonly keyType: "publishable" | "secret" | "unknown";
  /** Timeout par défaut en millisecondes pour les requêtes HTTP. */
  private readonly timeoutMs: number;
  /** Nombre max de retries automatiques sur 429/5xx (avec backoff). */
  private readonly maxRetries: number;

  /**
   * Crée une nouvelle instance du SDK Monegasy
   * @param config - Configuration du SDK
   * @throws {Error} Si la clé API n'est pas fournie
   */
  constructor(config: MonegasyConfig) {
    if (!config.apiKey) {
      throw new MonegasyValidationError(
        "API Key is required. Get your API key at https://wwww.monegasy.com",
      );
    }

    this.apiKey = config.apiKey;
    this.isSandbox = config.sandbox || false;
    this.timeoutMs = config.timeoutMs ?? 30_000;
    this.maxRetries = config.maxRetries ?? 3;

    // Détection du type via préfixe — utilisé pour throw early sur les
    // routes secret-only quand le SDK est init avec publishable.
    if (
      this.apiKey.startsWith("mk_pub_test_") ||
      this.apiKey.startsWith("mk_pub_live_")
    ) {
      this.keyType = "publishable";
    } else if (
      this.apiKey.startsWith("mk_sec_test_") ||
      this.apiKey.startsWith("mk_sec_live_") ||
      this.apiKey.startsWith("mk_test_") ||
      this.apiKey.startsWith("mk_live_")
    ) {
      this.keyType = "secret";
    } else {
      this.keyType = "unknown";
    }

    // ── Garde-fou sécurité : alerte si une clé secret est utilisée côté
    // navigateur. Le SDK Monegasy est conçu pour le frontend → seules les
    // clés publishable (mk_pub_*) doivent y figurer. Une clé secret
    // (mk_sec_*) ou une clé legacy (mk_test_*, mk_live_*) qui apparaît
    // dans du JS browser est forcément un risque sécurité.
    //
    // On ne throw pas (rétrocompat : certains marchands ont encore des
    // clés legacy en prod) — on warn en console pour qu'ils migrent.
    if (typeof window !== "undefined") {
      const isSecretLike =
        config.apiKey.startsWith("mk_sec_") ||
        config.apiKey.startsWith("mk_test_") ||
        config.apiKey.startsWith("mk_live_");
      if (isSecretLike) {
        console.warn(
          "⚠️  Monegasy SDK: Vous utilisez une clé secret ou legacy dans " +
            "le navigateur. Pour la sécurité, créez une publishable key " +
            "(mk_pub_*) restreinte à vos domaines depuis " +
            "https://monegasy.com/merchant/api-keys",
        );
      }
    }

    if (this.isSandbox) {
      this.baseURL = config.baseURL || SANDBOX_API_URL;
      this.webURL = config.webURL || SANDBOX_WEB_URL;
      console.info(
        "🔧 Monegasy SDK: Running in SANDBOX mode. Use sandbox: false for production.",
      );
    } else {
      this.baseURL = config.baseURL || DEFAULT_API_URL;
      this.webURL = config.webURL || DEFAULT_WEB_URL;
    }
  }

  /**
   * Retourne la version du SDK
   */
  static getVersion(): string {
    return SDK_VERSION;
  }

  /**
   * Vérifie si le SDK est en mode sandbox
   */
  get sandbox(): boolean {
    return this.isSandbox;
  }

  /**
   * Retourne l'URL de base de l'API
   */
  get apiUrl(): string {
    return this.baseURL;
  }

  /**
   * Retourne l'URL de la page web de paiement
   */
  get paymentWebUrl(): string {
    return this.webURL;
  }

  /**
   * Normalise une URL en `origin` (scheme + host + port). Utilisé pour
   * comparer `event.origin` de manière robuste : un trailing slash ou
   * un `www.` ne casse plus la comparaison.
   */
  private _normalizeOrigin(url: string): string {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  }

  /**
   * Renvoie `true` si l'origin du message vient bien de la page web Monegasy
   * configurée (`webURL`). Compare normalisé pour ignorer trailing slash.
   */
  private _isTrustedOrigin(messageOrigin: string): boolean {
    return (
      this._normalizeOrigin(messageOrigin) ===
      this._normalizeOrigin(this.webURL)
    );
  }

  /**
   * Wrapper interne de `fetch` qui retourne le JSON ou lance
   * une `MonegasyNetworkError` / `MonegasyApiError` typée.
   *
   * Améliorations vs un fetch direct :
   *  - Timeout configurable (défaut 30s, abort propre via AbortController)
   *  - Retry automatique sur 429 et 5xx avec backoff exponentiel
   *    (max `this.maxRetries`, honore le header `Retry-After`)
   *  - Idempotency-Key auto-généré sur POST/PUT/PATCH (UUID v4) si pas
   *    fourni explicitement par le caller
   *  - Erreurs typées (MonegasyApiError, MonegasyNetworkError)
   *
   * @param path - Chemin relatif à `baseURL` (commence par "/")
   * @param init - Options fetch (méthode, body, etc.)
   * @param signal - AbortSignal optionnel pour annuler la requête
   * @param options - Options SDK additionnelles (idempotencyKey explicite, etc.)
   */
  private async _apiFetch<T = unknown>(
    path: string,
    init: RequestInit = {},
    signal?: AbortSignal,
    options: { idempotencyKey?: string; skipIdempotency?: boolean } = {},
  ): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const isMutating =
      method === "POST" || method === "PUT" || method === "PATCH";

    const headers: Record<string, string> = {
      Authorization: `Bearer ${this.apiKey}`,
      ...(init.headers as Record<string, string> | undefined),
    };
    if (init.body && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    // Idempotency-Key auto sur les routes mutantes — protège contre les
    // doubles soumissions en cas de retry réseau côté client. Le caller
    // peut surcharger via `options.idempotencyKey` ou désactiver avec
    // `options.skipIdempotency`.
    if (
      isMutating &&
      !options.skipIdempotency &&
      !headers["Idempotency-Key"] &&
      !headers["idempotency-key"]
    ) {
      headers["Idempotency-Key"] =
        options.idempotencyKey ?? MonegasySDK._generateIdempotencyKey();
    } else if (options.idempotencyKey) {
      headers["Idempotency-Key"] = options.idempotencyKey;
    }

    // Tentative avec retry exponentiel
    let lastError: unknown = null;
    const maxAttempts = this.maxRetries + 1; // 1 essai + N retries

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Timeout via AbortController combiné avec le signal externe.
      const timeoutController = new AbortController();
      const timeoutId = setTimeout(
        () => timeoutController.abort(),
        this.timeoutMs,
      );
      const effectiveSignal = signal
        ? this._mergeSignals(signal, timeoutController.signal)
        : timeoutController.signal;

      let response: Response;
      try {
        response = await fetch(`${this.baseURL}${path}`, {
          ...init,
          headers,
          signal: effectiveSignal,
        });
      } catch (error) {
        clearTimeout(timeoutId);
        // AbortError (annulation manuelle OU timeout) → conservé tel quel
        if (
          error instanceof DOMException &&
          (error.name === "AbortError" || error.name === "TimeoutError")
        ) {
          // Si c'est le timeout interne et qu'on peut encore retry → on retry
          if (
            timeoutController.signal.aborted &&
            !signal?.aborted &&
            attempt < maxAttempts - 1
          ) {
            lastError = error;
            await this._backoffDelay(attempt);
            continue;
          }
          throw error;
        }
        // Les erreurs réseau (DNS down, no internet) ne sont PAS
        // retryées — c'est typiquement permanent sur la session.
        // Seules les erreurs HTTP transient (429, 5xx) sont retry.
        throw new MonegasyNetworkError(
          `Network error: Unable to connect to ${this.baseURL}. Check your internet connection.`,
        );
      }
      clearTimeout(timeoutId);

      // 429 ou 5xx → retry possible
      if (
        (response.status === 429 || response.status >= 500) &&
        attempt < maxAttempts - 1
      ) {
        // Guard `headers?.get?.()` : certains mocks de test ne fournissent
        // pas `headers` (undefined) — on tombe alors sur le backoff
        // exponentiel par défaut.
        const retryAfterHeader =
          response.headers && typeof response.headers.get === "function"
            ? response.headers.get("retry-after")
            : null;
        const retryAfter = parseInt(retryAfterHeader ?? "0", 10);
        const delayMs =
          retryAfter > 0
            ? retryAfter * 1000
            : Math.min(30_000, 2 ** attempt * 1000); // cap à 30s
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }

      if (!response.ok) {
        let data: unknown = null;
        let message = `Request failed (HTTP ${response.status})`;
        try {
          data = await response.json();
          const maybeMessage = (data as { message?: string })?.message;
          if (maybeMessage) message = maybeMessage;
        } catch {
          /* body non-JSON */
        }
        if (response.status === 404) {
          message =
            (data as { message?: string })?.message || `Resource not found`;
        }
        throw new MonegasyApiError(message, response.status, data);
      }

      return (await response.json()) as T;
    }

    // En théorie inatteignable car les paths au-dessus throw ou continue
    throw (
      lastError ??
      new MonegasyNetworkError("Request failed after all retries")
    );
  }

  /** Combine plusieurs AbortSignal en un seul. Si l'un abort, le merged abort. */
  private _mergeSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    for (const s of signals) {
      if (s.aborted) {
        controller.abort(s.reason);
        return controller.signal;
      }
      s.addEventListener("abort", () => controller.abort(s.reason), {
        once: true,
      });
    }
    return controller.signal;
  }

  /** Délai exponentiel + jitter (Math.random() ±20%) pour éviter le
   *  thundering herd quand plusieurs clients retentent en même temps. */
  private async _backoffDelay(attempt: number): Promise<void> {
    const base = Math.min(30_000, 2 ** attempt * 1000);
    const jitter = base * 0.2 * Math.random();
    await new Promise((resolve) => setTimeout(resolve, base + jitter));
  }

  /** Génère un UUID v4 simple (sans dépendance externe). Utilisé pour
   *  les Idempotency-Key auto. RFC 4122 v4. */
  private static _generateIdempotencyKey(): string {
    // crypto.randomUUID est dispo en Node 14.17+ et en navigateurs modernes.
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      try {
        return (crypto as { randomUUID: () => string }).randomUUID();
      } catch {
        /* fallback */
      }
    }
    // Fallback : pseudo-UUID v4
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /** Vérifie que la clé courante est de type secret. Throw early avec un
   *  message clair si publishable → évite un round-trip réseau pour
   *  recevoir un 403 du backend. */
  private _assertSecretKey(operation: string): void {
    if (this.keyType === "publishable") {
      throw new MonegasyValidationError(
        `Operation '${operation}' requires a SECRET API key (mk_sec_*). ` +
          "You initialized the SDK with a publishable key — switch to your secret key " +
          "(backend only, never expose in frontend code).",
      );
    }
  }

  /**
   * Crée un lien de paiement
   * @param params - Paramètres du paiement
   * @returns Promise<PaymentLink> - Le lien de paiement créé
   * @throws {Error} Si le montant est invalide ou la description manquante
   * @example
   * ```typescript
   * const payment = await sdk.createPaymentLink({
   *   amount: 50000,
   *   description: 'Achat iPhone 15',
   *   productName: 'iPhone 15 Pro'
   * });
   * console.log(payment.paymentUrl);
   * ```
   */
  async createPaymentLink(
    params: CreatePaymentLinkParams & { signal?: AbortSignal },
  ): Promise<PaymentLink> {
    const {
      amount,
      description,
      productName,
      productImage,
      metadata,
      successUrl,
      cancelUrl,
      webhookUrl,
      expiresInHours,
      signal,
    } = params;

    if (!amount || amount <= 0) {
      throw new MonegasyValidationError("Amount must be greater than 0 Ar");
    }
    if (!description || description.trim().length === 0) {
      throw new MonegasyValidationError("Description is required");
    }

    const metadataString =
      metadata && typeof metadata === "object"
        ? JSON.stringify(metadata)
        : metadata;

    return this._apiFetch<PaymentLink>(
      "/sdk/payment-links",
      {
        method: "POST",
        body: JSON.stringify({
          amount,
          description: description.trim(),
          productName: productName?.trim(),
          productImage,
          metadata: metadataString,
          successUrl,
          cancelUrl,
          webhookUrl,
          expiresInHours: expiresInHours || 24,
        }),
      },
      signal,
    );
  }

  /**
   * Récupère les détails d'un lien de paiement
   * @param linkId - ID du lien de paiement (ex: "pl_abc123")
   * @returns Promise<PaymentLink> - Les détails du paiement
   * @throws {Error} Si le paiement n'est pas trouvé
   * @example
   * ```typescript
   * const payment = await sdk.getPaymentLink('pl_abc123');
   * console.log(payment.status); // 'pending' | 'paid' | 'expired' | 'cancelled'
   * ```
   */
  async getPaymentLink(
    linkId: string,
    signal?: AbortSignal,
  ): Promise<PaymentLink> {
    if (!linkId || linkId.trim().length === 0) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    try {
      return await this._apiFetch<PaymentLink>(
        `/sdk/payment-links/${linkId.trim()}`,
        {},
        signal,
      );
    } catch (err) {
      // Enrichir le message d'erreur 404 avec le linkId pour faciliter
      // le debug côté caller (`Payment link "pl_xxx" not found`).
      if (err instanceof MonegasyApiError && err.statusCode === 404) {
        throw new MonegasyApiError(
          `Payment link "${linkId}" not found`,
          404,
          err.data,
        );
      }
      throw err;
    }
  }

  /**
   * Recherche dans le catalogue public Monegasy (tous marchands confondus).
   *
   * Endpoint public — ne consomme pas de quota. Pratique pour intégrer un
   * "marketplace" Monegasy dans une app tierce.
   *
   * @example
   * ```ts
   * const results = await sdk.searchProducts({ search: "café", lat: -18.91, lng: 47.52 });
   * results.items.forEach((p) => console.log(p.name, p.distanceKm, "km"));
   * ```
   */
  async searchProducts(
    params: SearchProductsParams = {},
    signal?: AbortSignal,
  ): Promise<CatalogResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.categoryId) query.set("categoryId", params.categoryId);
    if (params.lat != null) query.set("lat", String(params.lat));
    if (params.lng != null) query.set("lng", String(params.lng));
    if (params.radiusKm != null)
      query.set("radiusKm", String(params.radiusKm));
    if (params.page != null) query.set("page", String(params.page));
    if (params.pageSize != null) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();
    return this._apiFetch<CatalogResponse>(
      `/products/catalog${qs ? `?${qs}` : ""}`,
      {},
      signal,
    );
  }

  /**
   * Récupère la vitrine publique d'un marchand par son slug.
   *
   * Retourne le profil marchand + jusqu'à 100 produits actifs. Idéal pour
   * intégrer une page marchand dans un site externe ou une mini-app.
   *
   * @example
   * ```ts
   * const store = await sdk.getMerchantBySlug("bastih-store");
   * console.log(store.merchant.businessName, store.products.length);
   * ```
   */
  async getMerchantBySlug(
    slug: string,
    signal?: AbortSignal,
  ): Promise<StorefrontResponse> {
    if (!slug || slug.trim().length === 0) {
      throw new MonegasyValidationError("Merchant slug is required");
    }
    return this._apiFetch<StorefrontResponse>(
      `/storefront/${encodeURIComponent(slug.trim())}`,
      {},
      signal,
    );
  }

  /**
   * Alias de `getMerchantBySlug` — équivalent sémantique "liste des
   * produits d'un marchand", utile dans le contexte d'intégrateurs qui
   * veulent juste embarquer le catalogue d'un seul marchand.
   */
  async getMerchantProducts(
    slug: string,
    signal?: AbortSignal,
  ): Promise<StorefrontResponse["products"]> {
    const data = await this.getMerchantBySlug(slug, signal);
    return data.products;
  }

  /**
   * Vérifie périodiquement le statut d'un lien de paiement jusqu'à ce qu'il
   * passe en `paid` / `expired` / `cancelled`, ou que le timeout soit atteint.
   *
   * @param linkId - ID du lien de paiement
   * @param options - Callbacks et options de polling
   * @returns Promise<PaymentLink> - Le lien dans son état final
   *
   * @example
   * ```typescript
   * const final = await sdk.pollPaymentStatus("pl_abc123", {
   *   intervalMs: 3000,
   *   timeoutMs: 5 * 60 * 1000,
   *   onUpdate: (link) => console.log("Statut :", link.status),
   * });
   * if (final.status === "paid") {
   *   console.log("Paiement confirmé !");
   * }
   * ```
   */
  async pollPaymentStatus(
    linkId: string,
    options: {
      /** Intervalle entre deux checks (défaut 3000ms) */
      intervalMs?: number;
      /** Durée max avant timeout (défaut 5min) */
      timeoutMs?: number;
      /** Callback appelé à chaque update (avant le statut terminal) */
      onUpdate?: (link: PaymentLink) => void;
      /** Permet d'annuler le polling */
      signal?: AbortSignal;
    } = {},
  ): Promise<PaymentLink> {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    const intervalMs = options.intervalMs ?? 3000;
    const timeoutMs = options.timeoutMs ?? 5 * 60 * 1000;
    const start = Date.now();

    while (true) {
      if (options.signal?.aborted) {
        throw new DOMException("Polling cancelled", "AbortError");
      }
      if (Date.now() - start >= timeoutMs) {
        throw new MonegasyError(
          `Polling timeout after ${Math.round(timeoutMs / 1000)}s — payment not confirmed.`,
          "POLL_TIMEOUT",
        );
      }

      const link = await this.getPaymentLink(linkId);
      options.onUpdate?.(link);

      if (
        link.status === "paid" ||
        link.status === "expired" ||
        link.status === "cancelled"
      ) {
        return link;
      }

      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(resolve, intervalMs);
        if (options.signal) {
          const onAbort = () => {
            clearTimeout(t);
            reject(new DOMException("Polling cancelled", "AbortError"));
          };
          options.signal.addEventListener("abort", onAbort, { once: true });
        }
      });
    }
  }

  /**
   * Ouvre le paiement dans une popup centrée
   * @param linkId - ID du lien de paiement
   * @param options - Options de la popup (largeur, hauteur)
   * @returns Window | null - La fenêtre popup ou null si bloquée
   * @example
   * ```typescript
   * const popup = sdk.openPaymentPopup('pl_abc123', {
   *   width: 500,
   *   height: 700
   * });
   * ```
   */
  openPaymentPopup(linkId: string, options: PopupOptions = {}): Window | null {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }

    const url = `${this.webURL}/pay/${linkId}`;
    const width = options.width || 500;
    const height = options.height || 700;
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);

    const popup = window.open(
      url,
      "monegasy-payment",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no`
    );

    if (!popup) {
      console.warn(
        "Monegasy SDK: Popup blocked. Redirecting to payment page..."
      );
      window.location.href = url;
    }

    return popup;
  }

  /**
   * Redirige vers la page de paiement
   * @param linkId - ID du lien de paiement
   * @example
   * ```typescript
   * sdk.redirectToPayment('pl_abc123');
   * ```
   */
  redirectToPayment(linkId: string): void {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    window.location.href = `${this.webURL}/pay/${linkId}`;
  }

  /**
   * Génère l'URL de paiement pour un lien donné
   * @param linkId - ID du lien de paiement
   * @returns string - URL complète de paiement
   */
  getPaymentUrl(linkId: string): string {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    return `${this.webURL}/pay/${linkId}`;
  }

  /**
   * Crée et affiche un bouton de paiement stylisé
   * @param params - Paramètres du bouton et du paiement
   * @param container - Sélecteur CSS du conteneur (ex: "#payment-button")
   * @returns Promise<HTMLButtonElement> - Le bouton créé
   * @throws {Error} Si le conteneur n'est pas trouvé
   * @example
   * ```typescript
   * await sdk.renderPaymentButton({
   *   amount: 50000,
   *   description: 'Achat produit',
   *   buttonText: 'Payer maintenant',
   *   onSuccess: (payment) => console.log('Payé!', payment),
   *   onError: (error) => console.error('Erreur:', error),
   *   onCancel: () => console.log('Annulé')
   * }, '#payment-container');
   * ```
   */
  async renderPaymentButton(
    params: RenderButtonParams,
    container: string | HTMLElement
  ): Promise<HTMLButtonElement> {
    const {
      amount,
      description,
      productName,
      buttonText,
      buttonStyle,
      onSuccess,
      onError,
      onCancel,
    } = params;

    try {
      // Créer le lien de paiement
      const payment = await this.createPaymentLink({
        amount,
        description,
        productName,
        productImage: params.productImage,
        metadata: params.metadata,
        successUrl: params.successUrl,
        cancelUrl: params.cancelUrl,
        webhookUrl: params.webhookUrl,
        expiresInHours: params.expiresInHours,
      });

      // Créer le bouton
      const button = document.createElement("button");
      button.textContent =
        buttonText || `Payer ${amount.toLocaleString("fr-FR")} Ar`;
      button.className = "monegasy-payment-button";
      button.setAttribute("data-monegasy-link", payment.linkId);
      button.setAttribute("data-monegasy-amount", String(amount));

      // Styles par défaut — brand Monegasy (cohérent avec renderSmartPaymentButton)
      const defaultStyle: Record<string, string> = {
        background: "linear-gradient(135deg, #1F8A65 0%, #005C42 100%)",
        color: "white",
        border: "none",
        borderRadius: "12px",
        padding: "14px 28px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 14px rgba(31, 138, 101, 0.4)",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        minWidth: "200px",
      };

      // Appliquer les styles (défaut + personnalisés)
      Object.assign(button.style, defaultStyle, buttonStyle || {});

      // Hover effect
      button.addEventListener("mouseenter", () => {
        button.style.transform = "translateY(-2px) scale(1.02)";
        button.style.boxShadow = "0 6px 20px rgba(31, 138, 101, 0.5)";
      });

      button.addEventListener("mouseleave", () => {
        button.style.transform = "translateY(0) scale(1)";
        button.style.boxShadow = "0 4px 14px rgba(31, 138, 101, 0.4)";
      });

      // Active effect
      button.addEventListener("mousedown", () => {
        button.style.transform = "translateY(0) scale(0.98)";
      });

      button.addEventListener("mouseup", () => {
        button.style.transform = "translateY(-2px) scale(1.02)";
      });

      // Click handler
      button.addEventListener("click", () => {
        const popup = this.openPaymentPopup(payment.linkId);
        let checkPopupInterval: ReturnType<typeof setInterval>;

        // Écouter les messages de la popup
        const messageHandler = (event: MessageEvent) => {
          // Sécurité: vérifier l'origine
          if (!this._isTrustedOrigin(event.origin)) return;

          const { type, payment: paymentData, error } = event.data || {};

          if (type === "monegasy-payment-success") {
            if (onSuccess) onSuccess(paymentData);
            cleanup();
          } else if (type === "monegasy-payment-error") {
            if (onError) onError(new Error(error?.message || "Payment failed"));
            cleanup();
          } else if (type === "monegasy-payment-cancel") {
            if (onCancel) onCancel();
            cleanup();
          }
        };

        const cleanup = () => {
          if (popup && !popup.closed) popup.close();
          clearInterval(checkPopupInterval);
          window.removeEventListener("message", messageHandler);
        };

        window.addEventListener("message", messageHandler);

        // Vérifier périodiquement si la popup est fermée manuellement
        checkPopupInterval = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(checkPopupInterval);
            window.removeEventListener("message", messageHandler);
            if (onCancel) onCancel();
          }
        }, 500);
      });

      // Ajouter au DOM
      let containerElement: Element | null;

      if (typeof container === "string") {
        containerElement = document.querySelector(container);
        if (!containerElement) {
          throw new MonegasyValidationError(`Container "${container}" not found in the document. Make sure the element exists.`);
        }
      } else {
        containerElement = container;
      }

      containerElement.appendChild(button);

      return button;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (onError) onError(err);
      throw err;
    }
  }

  /**
   * Formate un montant en Ariary
   * @param amount - Montant à formater
   * @returns string - Montant formaté (ex: "50 000 Ar")
   */
  static formatAmount(amount: number): string {
    return `${amount.toLocaleString("fr-FR")} Ar`;
  }

  // ===================== REMBOURSEMENTS =====================

  /**
   * Émet un remboursement sur une transaction antérieure.
   *
   * ⚠️ Nécessite une **secret key** (mk_sec_*) — le SDK throw early
   * si initialisé avec une publishable.
   *
   * @example
   * ```ts
   * await sdk.refund({
   *   transactionId: "tx_abc123",
   *   amount: 50000,           // optionnel — full refund si omis
   *   reason: "Article défectueux",
   * });
   * ```
   */
  async refund(params: {
    transactionId: string;
    amount?: number;
    reason?: string;
  }): Promise<{ refundId: string; status: string; amount: number }> {
    this._assertSecretKey("refund");
    if (!params.transactionId) {
      throw new MonegasyValidationError("transactionId is required");
    }
    return this._apiFetch("/wallet/refund", {
      method: "POST",
      body: JSON.stringify({
        originalTransactionId: params.transactionId,
        amount: params.amount,
        description: params.reason,
      }),
    });
  }

  // ===================== WEBHOOKS =====================

  /**
   * Configure (ou met à jour) l'URL et les événements écoutés du webhook
   * du marchand. Génère ou conserve le webhook secret automatiquement.
   *
   * ⚠️ Nécessite une **secret key**.
   *
   * @example
   * ```ts
   * await sdk.configureWebhook({
   *   url: "https://votre-site.com/webhooks/monegasy",
   *   events: ["payment.completed", "payment.failed", "installment.paid"],
   *   retryCount: 3,
   * });
   * ```
   */
  async configureWebhook(params: {
    url: string;
    events: string[];
    retryCount?: number;
  }): Promise<WebhookConfig> {
    this._assertSecretKey("configureWebhook");
    return this._apiFetch<WebhookConfig>("/webhooks/configure", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  /** Récupère la config webhook courante du marchand. */
  async getWebhookConfig(): Promise<WebhookConfig> {
    this._assertSecretKey("getWebhookConfig");
    return this._apiFetch<WebhookConfig>("/webhooks");
  }

  /** Récupère l'historique des envois webhook (success / fail / retry). */
  async getWebhookLogs(params: { page?: number; limit?: number } = {}): Promise<{
    logs: WebhookLog[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    this._assertSecretKey("getWebhookLogs");
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return this._apiFetch(`/webhooks/logs${q ? `?${q}` : ""}`);
  }

  /**
   * Désactive le webhook actuel (passe `isActive: false`). Réactivable
   * via `configureWebhook(...)`.
   */
  async disableWebhook(): Promise<void> {
    this._assertSecretKey("disableWebhook");
    await this._apiFetch("/webhooks", { method: "DELETE" });
  }

  /**
   * Envoie un événement de test (du type souhaité) à votre URL webhook.
   * Idéal pour vérifier votre intégration sans attendre une vraie
   * transaction. La signature HMAC est calculée comme en prod.
   *
   * @example
   * ```ts
   * const result = await sdk.testWebhookEvent("payment.completed");
   * console.log(result.statusCode, result.latencyMs);
   * ```
   */
  async testWebhookEvent(
    eventType: string,
    customData?: Record<string, unknown>,
  ): Promise<WebhookTestResult> {
    this._assertSecretKey("testWebhookEvent");
    return this._apiFetch<WebhookTestResult>("/webhooks/test-event", {
      method: "POST",
      body: JSON.stringify({ eventType, customData }),
      // Le test webhook n'a pas besoin d'idempotency (chaque test est volontairement
      // ré-exécutable pour debug).
    }, undefined, { skipIdempotency: true });
  }

  // ===================== FACILITÉS DE PAIEMENT =====================

  /**
   * Calcule les options de facilité de paiement disponibles pour un
   * montant donné chez un marchand. Renvoie un tableau vide si aucune
   * facilité n'est applicable (montant hors bornes, modes non activés).
   *
   * Accessible avec une publishable key — utile pour afficher les
   * options de paiement étalé sur votre page produit avant que le
   * client clique sur "Payer".
   */
  async previewInstallments(params: {
    merchantId: string;
    amount: number;
    allowedModes?: InstallmentMode[];
    depositPercentage?: number;
    balanceDueDays?: number;
  }): Promise<InstallmentPreview[]> {
    if (!params.merchantId) {
      throw new MonegasyValidationError("merchantId is required");
    }
    if (!params.amount || params.amount <= 0) {
      throw new MonegasyValidationError("amount must be > 0");
    }
    return this._apiFetch<InstallmentPreview[]>("/installments/preview", {
      method: "POST",
      body: JSON.stringify(params),
    }, undefined, { skipIdempotency: true });
  }

  /** Récupère la config des facilités du marchand. ⚠️ Secret key. */
  async getInstallmentConfig(): Promise<MerchantInstallmentConfig> {
    this._assertSecretKey("getInstallmentConfig");
    return this._apiFetch<MerchantInstallmentConfig>(
      "/installments/merchant/config",
    );
  }

  /** Met à jour la config des facilités. ⚠️ Secret key. */
  async updateInstallmentConfig(
    config: Partial<MerchantInstallmentConfig>,
  ): Promise<MerchantInstallmentConfig> {
    this._assertSecretKey("updateInstallmentConfig");
    return this._apiFetch<MerchantInstallmentConfig>(
      "/installments/merchant/config",
      {
        method: "PATCH",
        body: JSON.stringify(config),
      },
    );
  }

  /** Liste les plans d'échéancier du marchand. ⚠️ Secret key. */
  async getInstallmentPlans(params: {
    status?: "active" | "completed" | "failed" | "cancelled";
    page?: number;
    limit?: number;
  } = {}): Promise<{
    plans: InstallmentPlanInfo[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    this._assertSecretKey("getInstallmentPlans");
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    const q = qs.toString();
    return this._apiFetch(
      `/installments/merchant/plans${q ? `?${q}` : ""}`,
    );
  }

  /** Détail d'un plan + ses échéances. ⚠️ Secret key. */
  async getInstallmentPlan(planId: string): Promise<InstallmentPlanInfo> {
    this._assertSecretKey("getInstallmentPlan");
    return this._apiFetch<InstallmentPlanInfo>(
      `/installments/merchant/plans/${planId}`,
    );
  }

  /** Annule un plan en cours. Les échéances déjà payées ne sont PAS
   *  remboursées automatiquement. ⚠️ Secret key. */
  async cancelInstallmentPlan(
    planId: string,
    reason?: string,
  ): Promise<InstallmentPlanInfo> {
    this._assertSecretKey("cancelInstallmentPlan");
    return this._apiFetch<InstallmentPlanInfo>(
      `/installments/merchant/plans/${planId}/cancel`,
      {
        method: "POST",
        body: JSON.stringify({ reason }),
      },
    );
  }

  /** Échéances dues sous N jours — pour widget dashboard. ⚠️ Secret key. */
  async getUpcomingInstallments(
    withinDays = 7,
  ): Promise<InstallmentLineInfo[]> {
    this._assertSecretKey("getUpcomingInstallments");
    return this._apiFetch<InstallmentLineInfo[]>(
      `/installments/merchant/upcoming?withinDays=${withinDays}`,
    );
  }

  // ===================== REQUEST SIGNING (HMAC) =====================

  /**
   * Génère les headers de signature HMAC pour une requête sortante.
   *
   * À utiliser depuis votre **backend** pour appeler les routes très
   * sensibles de Monegasy qui exigent la signature (refunds, modifs
   * config, opérations admin). La signature résiste au replay (timestamp
   * ±5min) et garantit que vous êtes bien le propriétaire de la clé.
   *
   * @example
   * ```ts
   * import { MonegasySDK } from "monegasy-js";
   *
   * const sdk = new MonegasySDK({ apiKey: process.env.MONEGASY_SECRET! });
   * const body = { amount: 50000, description: "Test" };
   * const headers = sdk.signRequest(body);
   *
   * await fetch("https://api.monegasy.com/sdk/payment-links", {
   *   method: "POST",
   *   headers: { "Content-Type": "application/json", ...headers },
   *   body: JSON.stringify(body),
   * });
   * ```
   *
   * Renvoie `{ Authorization, X-Monegasy-Timestamp, X-Monegasy-Signature }`.
   */
  signRequest(body: unknown): {
    Authorization: string;
    "X-Monegasy-Timestamp": string;
    "X-Monegasy-Signature": string;
  } {
    if (typeof window !== "undefined" && typeof process === "undefined") {
      throw new MonegasyValidationError(
        "signRequest() must be called from a backend (Node.js). " +
          "Signing in the browser exposes your secret key.",
      );
    }
    this._assertSecretKey("signRequest");

    const timestamp = Math.floor(Date.now() / 1000).toString();
    const payload = `${timestamp}.${
      body ? JSON.stringify(body) : ""
    }`;
    // Lazy import de node:crypto (uniquement Node.js)
    let signature: string;
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const nodeCrypto = require("node:crypto") as typeof import("crypto");
      signature =
        "sha256=" +
        nodeCrypto
          .createHmac("sha256", this.apiKey)
          .update(payload)
          .digest("hex");
    } catch {
      throw new MonegasyValidationError(
        "signRequest() requires node:crypto. Make sure you're running in Node.js >= 14.17.",
      );
    }

    return {
      Authorization: `Bearer ${this.apiKey}`,
      "X-Monegasy-Timestamp": timestamp,
      "X-Monegasy-Signature": signature,
    };
  }

  // ===================== ABONNEMENTS =====================

  /**
   * Crée un plan d'abonnement
   * @param params - Paramètres du plan
   * @returns Promise<SubscriptionPlan> - Le plan créé
   * @throws {Error} Si les paramètres sont invalides
   * @example
   * ```typescript
   * const plan = await sdk.createSubscriptionPlan({
   *   name: 'Premium',
   *   price: 50000,
   *   billingPeriod: 'monthly',
   *   trialDays: 7,
   *   features: ['Accès illimité', 'Support prioritaire']
   * });
   * ```
   */
  async createSubscriptionPlan(
    params: CreateSubscriptionPlanParams,
    signal?: AbortSignal,
  ): Promise<SubscriptionPlan> {
    const { name, description, price, billingPeriod, trialDays, features } =
      params;

    if (!name || name.trim().length === 0) {
      throw new MonegasyValidationError("Plan name is required");
    }
    if (!price || price <= 0) {
      throw new MonegasyValidationError("Price must be greater than 0 Ar");
    }
    if (!billingPeriod) {
      throw new MonegasyValidationError("Billing period is required");
    }

    const data = await this._apiFetch<{ plan: SubscriptionPlan }>(
      "/sdk/subscriptions/plans",
      {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          description: description?.trim(),
          price,
          billingPeriod,
          trialDays: trialDays || 0,
          features: features || [],
        }),
      },
      signal,
    );
    return data.plan;
  }

  /**
   * Récupère tous les plans d'abonnement du merchant
   * @returns Promise<SubscriptionPlan[]> - Liste des plans
   * @example
   * ```typescript
   * const plans = await sdk.getSubscriptionPlans();
   * plans.forEach(plan => console.log(plan.name, plan.price));
   * ```
   */
  async getSubscriptionPlans(
    signal?: AbortSignal,
  ): Promise<SubscriptionPlan[]> {
    try {
      const data = await this._apiFetch<{ plans: SubscriptionPlan[] }>(
        "/sdk/subscriptions/plans",
        {},
        signal,
      );
      return data.plans;
    } catch (err) {
      // Wrapper d'erreur pour offrir un message contextuel (le test
      // attend "Failed to get subscription plans" qui aide à pinpoint
      // dans des logs d'app marchande).
      if (err instanceof MonegasyApiError) {
        throw new MonegasyApiError(
          `Failed to get subscription plans: ${err.message}`,
          err.statusCode,
          err.data,
        );
      }
      throw err;
    }
  }

  /**
   * Récupère un plan d'abonnement par son ID
   * @param planId - ID du plan
   * @returns Promise<SubscriptionPlan> - Détails du plan
   * @example
   * ```typescript
   * const plan = await sdk.getSubscriptionPlan('plan_123');
   * console.log(plan.subscriberCount);
   * ```
   */
  async getSubscriptionPlan(
    planId: string,
    signal?: AbortSignal,
  ): Promise<SubscriptionPlan> {
    if (!planId) {
      throw new MonegasyValidationError("Plan ID is required");
    }
    try {
      const data = await this._apiFetch<{ plan: SubscriptionPlan }>(
        `/sdk/subscriptions/plans/${planId}`,
        {},
        signal,
      );
      return data.plan;
    } catch (err) {
      if (err instanceof MonegasyApiError && err.statusCode === 404) {
        throw new MonegasyApiError(
          `Subscription plan "${planId}" not found`,
          404,
          err.data,
        );
      }
      throw err;
    }
  }

  /**
   * Met à jour un plan d'abonnement
   * @param planId - ID du plan
   * @param params - Paramètres à mettre à jour
   * @returns Promise<SubscriptionPlan> - Plan mis à jour
   * @example
   * ```typescript
   * const plan = await sdk.updateSubscriptionPlan('plan_123', {
   *   price: 75000,
   *   features: ['Nouvelles fonctionnalités']
   * });
   * ```
   */
  async updateSubscriptionPlan(
    planId: string,
    params: UpdateSubscriptionPlanParams,
    signal?: AbortSignal,
  ): Promise<SubscriptionPlan> {
    if (!planId) {
      throw new MonegasyValidationError("Plan ID is required");
    }
    const data = await this._apiFetch<{ plan: SubscriptionPlan }>(
      `/sdk/subscriptions/plans/${planId}`,
      {
        method: "PUT",
        body: JSON.stringify(params),
      },
      signal,
    );
    return data.plan;
  }

  /**
   * Supprime un plan d'abonnement
   * @param planId - ID du plan
   * @throws {Error} Si le plan a des abonnés actifs
   * @example
   * ```typescript
   * await sdk.deleteSubscriptionPlan('plan_123');
   * ```
   */
  async deleteSubscriptionPlan(
    planId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!planId) {
      throw new MonegasyValidationError("Plan ID is required");
    }
    await this._apiFetch<void>(
      `/sdk/subscriptions/plans/${planId}`,
      { method: "DELETE" },
      signal,
    );
  }

  /**
   * Récupère la liste des abonnés
   * @returns Promise<Subscriber[]> - Liste des abonnés
   * @example
   * ```typescript
   * const subscribers = await sdk.getSubscribers();
   * console.log(`${subscribers.length} abonnés`);
   * ```
   */
  async getSubscribers(signal?: AbortSignal): Promise<Subscriber[]> {
    const data = await this._apiFetch<{ subscribers: Subscriber[] }>(
      "/sdk/subscriptions/subscribers",
      {},
      signal,
    );
    return data.subscribers;
  }

  /**
   * Récupère les statistiques des abonnements
   * @returns Promise<SubscriptionStats> - Statistiques
   * @example
   * ```typescript
   * const stats = await sdk.getSubscriptionStats();
   * console.log(`Revenu mensuel: ${stats.revenue.monthlyEstimate} Ar`);
   * ```
   */
  async getSubscriptionStats(
    signal?: AbortSignal,
  ): Promise<SubscriptionStats> {
    const data = await this._apiFetch<{ stats: SubscriptionStats }>(
      "/sdk/subscriptions/stats",
      {},
      signal,
    );
    return data.stats;
  }

  /**
   * Génère un lien d'inscription pour un plan
   * @param planId - ID du plan
   * @returns Promise<SubscribeLinks> - Liens d'inscription
   * @example
   * ```typescript
   * const links = await sdk.getSubscribeLink('plan_123');
   * console.log(links.links.webUrl);
   * ```
   */
  async getSubscribeLink(
    planId: string,
    signal?: AbortSignal,
  ): Promise<SubscribeLinks> {
    if (!planId) {
      throw new MonegasyValidationError("Plan ID is required");
    }
    return this._apiFetch<SubscribeLinks>(
      `/sdk/subscriptions/plans/${planId}/subscribe-link`,
      {},
      signal,
    );
  }

  // ===================== PLATEFORME (PARTENAIRE / CONNECT) =====================

  /**
   * API PARTENAIRE (« Connect ») — réservée aux clés PLATEFORME (secret).
   *
   * Permet à une app verticale (pharmacie, hôtel…) de provisionner ses pros
   * comme sous-marchands Monegasy et d'encaisser pour eux. Les fonds vont dans
   * le wallet du sous-marchand (commission Monegasy normale, pas de split).
   *
   * @example
   * ```typescript
   * const monegasy = new MonegasySDK({ apiKey: "mk_sec_live_..." }); // clé PLATEFORME
   *
   * // À l'inscription pro sur ton app → compte Monegasy créé en même temps :
   * const pro = await monegasy.platform.createMerchant({
   *   phone: "0340000000",
   *   businessName: "Pharmacie Avaratra",
   *   externalRef: "pharma_user_42",
   * });
   *
   * // Encaisser pour lui :
   * const link = await monegasy.platform.createPaymentLink({
   *   merchantId: pro.merchantId,
   *   amount: 15000,
   *   description: "Commande #1234",
   * });
   * ```
   */
  get platform() {
    return {
      /** Provisionne (ou lie) un sous-marchand à partir de son numéro. */
      createMerchant: async (
        params: CreatePlatformMerchantParams,
        signal?: AbortSignal,
      ): Promise<PlatformMerchant> => {
        this._assertSecretKey("platform.createMerchant");
        if (!params.phone || params.phone.trim().length === 0) {
          throw new MonegasyValidationError("phone is required");
        }
        if (!params.businessName || params.businessName.trim().length === 0) {
          throw new MonegasyValidationError("businessName is required");
        }
        return this._apiFetch<PlatformMerchant>(
          "/sdk/platform/merchants",
          {
            method: "POST",
            body: JSON.stringify({
              phoneNumber: params.phone.trim(),
              businessName: params.businessName.trim(),
              businessDescription: params.businessDescription,
              businessAddress: params.businessAddress,
              businessEmail: params.businessEmail,
              externalRef: params.externalRef,
            }),
          },
          signal,
        );
      },

      /** Liste les sous-marchands de la plateforme. */
      listMerchants: async (
        signal?: AbortSignal,
      ): Promise<PlatformSubMerchant[]> => {
        this._assertSecretKey("platform.listMerchants");
        return this._apiFetch<PlatformSubMerchant[]>(
          "/sdk/platform/merchants",
          {},
          signal,
        );
      },

      /** Crée un lien de paiement POUR un sous-marchand (fonds → SON wallet). */
      createPaymentLink: async (
        params: CreatePlatformPaymentLinkParams,
        signal?: AbortSignal,
      ): Promise<PaymentLink> => {
        this._assertSecretKey("platform.createPaymentLink");
        const { merchantId, amount, description } = params;
        if (!merchantId || merchantId.trim().length === 0) {
          throw new MonegasyValidationError("merchantId is required");
        }
        if (!amount || amount <= 0) {
          throw new MonegasyValidationError("Amount must be greater than 0 Ar");
        }
        if (!description || description.trim().length === 0) {
          throw new MonegasyValidationError("Description is required");
        }
        const metadataString =
          params.metadata && typeof params.metadata === "object"
            ? JSON.stringify(params.metadata)
            : params.metadata;
        return this._apiFetch<PaymentLink>(
          "/sdk/platform/payment-links",
          {
            method: "POST",
            body: JSON.stringify({
              merchantId: merchantId.trim(),
              amount,
              description: description.trim(),
              productName: params.productName?.trim(),
              productImage: params.productImage,
              metadata: metadataString,
              successUrl: params.successUrl,
              cancelUrl: params.cancelUrl,
              webhookUrl: params.webhookUrl,
              expiresInHours: params.expiresInHours || 24,
            }),
          },
          signal,
        );
      },
    };
  }

  // ===================== WEBHOOK VERIFICATION =====================

  /**
   * Vérifie la signature d'un webhook Monegasy (côté serveur uniquement - Node.js)
   *
   * IMPORTANT: Cette méthode est destinée à être utilisée côté serveur (Node.js)
   * pour valider que le webhook provient bien de Monegasy.
   *
   * @param payload - Le body brut de la requête (string JSON)
   * @param signature - La valeur du header "X-Monegasy-Signature"
   * @param secret - Le secret webhook de votre dashboard Monegasy
   * @returns boolean - true si la signature est valide
   * @throws {Error} Si l'environnement Node.js n'est pas disponible
   *
   * @example
   * ```typescript
   * // Express.js / Next.js API Route
   * import { MonegasySDK } from 'monegasy-js';
   *
   * app.post('/api/webhooks/monegasy', (req, res) => {
   *   const signature = req.headers['x-monegasy-signature'];
   *   const rawBody = JSON.stringify(req.body);
   *
   *   const isValid = MonegasySDK.verifyWebhookSignature(
   *     rawBody,
   *     signature,
   *     process.env.MONEGASY_WEBHOOK_SECRET
   *   );
   *
   *   if (!isValid) {
   *     return res.status(401).json({ error: 'Invalid signature' });
   *   }
   *
   *   // Traiter le webhook en toute sécurité...
   *   const { event, data } = req.body;
   *
   *   if (event === 'payment.completed') {
   *     // Enregistrer la commande dans votre base de données
   *     await saveOrder(data);
   *   }
   *
   *   res.status(200).json({ received: true });
   * });
   * ```
   */
  static verifyWebhookSignature(
    payload: string,
    signature: string,
    secret: string
  ): boolean {
    if (!payload || !signature || !secret) {
      throw new MonegasyValidationError("MonegasySDK.verifyWebhookSignature: payload, signature, and secret are all required");
    }

    // Garde-fou navigateur : `crypto.createHmac` / `Buffer` n'existent que
    // dans Node.js. On vérifie côté browser sans appeler `require` (qui n'est
    // pas défini en ESM strict / dans certains bundlers).
    const isNode =
      typeof process !== "undefined" &&
      process.versions != null &&
      process.versions.node != null;
    if (!isNode) {
      throw new MonegasyError(
        "MonegasySDK.verifyWebhookSignature is only available in Node.js (server-side). Do not verify webhook signatures in the browser.",
        "BROWSER_NOT_SUPPORTED",
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const nodeCrypto = require("node:crypto") as typeof import("node:crypto");
    const expectedSignature = nodeCrypto
      .createHmac("sha256", secret)
      .update(payload)
      .digest("hex");

    if (expectedSignature.length !== signature.length) {
      return false;
    }

    const a = Buffer.from(expectedSignature, "hex");
    const b = Buffer.from(signature, "hex");
    return nodeCrypto.timingSafeEqual(a, b);
  }

  /**
   * Parse et type un payload webhook Monegasy
   *
   * @param body - Le body JSON parsé de la requête webhook
   * @returns WebhookPayload typé selon l'événement
   *
   * @example
   * ```typescript
   * const webhook = MonegasySDK.parseWebhookPayload(req.body);
   *
   * switch (webhook.event) {
   *   case 'payment.completed':
   *     console.log('Paiement reçu:', webhook.data.amount, 'Ar');
   *     console.log('Transaction:', webhook.data.transactionId);
   *     break;
   *   case 'payment.failed':
   *     console.log('Paiement échoué:', webhook.data.error);
   *     break;
   * }
   * ```
   */
  static parseWebhookPayload(body: any): WebhookPayload {
    if (!body || !body.event || !body.data) {
      throw new MonegasyValidationError("Invalid webhook payload: missing 'event' or 'data' field");
    }

    return body as WebhookPayload;
  }

  // ===================== DEVICE DETECTION & DEEP LINKING =====================

  /**
   * Détecte les informations sur le device de l'utilisateur
   * @returns DeviceInfo - Informations sur le device
   * @example
   * ```typescript
   * const device = sdk.detectDevice();
   * if (device.isMobile) {
   *   console.log(`Ouvrir sur ${device.storeName}`);
   * }
   * ```
   */
  static detectDevice(): DeviceInfo {
    const ua =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isMobile =
      isIOS ||
      isAndroid ||
      /webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);

    return {
      isMobile,
      isIOS,
      isAndroid,
      isTablet,
      isDesktop: !isMobile,
      storeUrl: isIOS ? APP_STORE_URL : PLAY_STORE_URL,
      storeName: isIOS ? "App Store" : "Google Play",
      userAgent: ua,
    };
  }

  /**
   * Instance method pour détecter le device
   */
  detectDevice(): DeviceInfo {
    return MonegasySDK.detectDevice();
  }

  /**
   * Génère un deep link pour ouvrir l'app mobile
   * @param linkId - ID du lien de paiement
   * @returns string - Deep link URL
   * @example
   * ```typescript
   * const deepLink = sdk.generateDeepLink('pl_abc123');
   * // => "monegasy://pay/pl_abc123"
   * ```
   */
  generateDeepLink(linkId: string): string {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    return `${DEEP_LINK_SCHEME}://pay/${linkId}`;
  }

  /**
   * Génère un Universal Link (iOS) / App Link (Android)
   * @param linkId - ID du lien de paiement
   * @returns string - Universal/App link URL
   * @example
   * ```typescript
   * const link = sdk.generateUniversalLink('pl_abc123');
   * // => "https://monegasy.com/pay/pl_abc123"
   * ```
   */
  generateUniversalLink(linkId: string): string {
    if (!linkId) {
      throw new MonegasyValidationError("Payment link ID is required");
    }
    // Utiliser DEFAULT_WEB_URL pour production, webURL pour sandbox
    const baseUrl = this.isSandbox ? this.webURL : DEFAULT_WEB_URL;
    return `${baseUrl}/pay/${linkId}`;
  }

  /**
   * Tente d'ouvrir l'application mobile avec fallback
   * @param linkId - ID du lien de paiement
   * @param options - Options pour le comportement de fallback
   * @returns Promise<boolean> - true si l'app s'est ouverte, false sinon
   * @example
   * ```typescript
   * const opened = await sdk.openMobileApp('pl_abc123', {
   *   fallbackToStore: true,
   *   fallbackDelay: 2000
   * });
   * ```
   */
  async openMobileApp(
    linkId: string,
    options: {
      /** Rediriger vers le store si l'app n'est pas installée */
      fallbackToStore?: boolean;
      /** Délai avant de considérer que l'app n'est pas installée (ms) */
      fallbackDelay?: number;
      /** Callback si l'app n'est pas installée */
      onNotInstalled?: () => void;
    } = {}
  ): Promise<boolean> {
    const {
      fallbackToStore = true,
      fallbackDelay = 2000,
      onNotInstalled,
    } = options;

    const device = MonegasySDK.detectDevice();

    if (!device.isMobile) {
      console.warn("openMobileApp called on non-mobile device");
      return false;
    }

    const deepLink = this.generateDeepLink(linkId);
    const startTime = Date.now();

    // Tenter d'ouvrir l'app
    window.location.href = deepLink;

    return new Promise((resolve) => {
      const checkTimeout = setTimeout(() => {
        const elapsed = Date.now() - startTime;

        // Si la page a toujours le focus après le délai, l'app n'est probablement pas installée
        if (elapsed >= fallbackDelay && document.hasFocus()) {
          console.log("App not installed, triggering fallback");

          if (onNotInstalled) {
            onNotInstalled();
          }

          if (fallbackToStore) {
            window.location.href = device.storeUrl;
          }

          resolve(false);
        }
      }, fallbackDelay);

      // Si la page perd le focus, l'app s'est probablement ouverte
      const handleBlur = () => {
        clearTimeout(checkTimeout);
        window.removeEventListener("blur", handleBlur);
        resolve(true);
      };

      window.addEventListener("blur", handleBlur);

      // Nettoyer après un temps max
      setTimeout(() => {
        clearTimeout(checkTimeout);
        window.removeEventListener("blur", handleBlur);
      }, fallbackDelay + 1000);
    });
  }

  /**
   * Crée un bouton de paiement intelligent avec détection mobile
   * Sur mobile: tente d'ouvrir l'app, sinon propose le téléchargement
   * Sur desktop: affiche le QR Code ou redirige vers la page de paiement
   * @param params - Paramètres du paiement
   * @param container - Conteneur pour le bouton
   * @returns Promise<HTMLButtonElement>
   */
  async renderSmartPaymentButton(
    params: RenderButtonParams & {
      /**
       * Mode d'affichage du paiement:
       * - 'iframe'    : overlay iframe sur la page du merchant (recommandé)
       * - 'popup'     : nouvelle fenêtre popup
       * - 'redirect'  : redirection complète vers la page de paiement
       * - 'deeplink'  : deep link mobile uniquement
       * Défaut: 'iframe'
       */
      mode?: "iframe" | "popup" | "redirect" | "deeplink";
      /** @deprecated Utiliser `mode` à la place */
      mobileAction?: "deeplink" | "redirect";
    },
    container: string | HTMLElement
  ): Promise<HTMLButtonElement> {
    const device = MonegasySDK.detectDevice();

    // Créer d'abord le lien de paiement
    const payment = await this.createPaymentLink(params);

    // Créer le bouton
    const button = document.createElement("button");
    button.textContent =
      params.buttonText ||
      `Payer ${params.amount.toLocaleString("fr-FR")} Ar`;
    button.className = "monegasy-payment-button";
    button.setAttribute("data-monegasy-link", payment.linkId);
    button.setAttribute("data-monegasy-amount", String(params.amount));
    button.setAttribute(
      "data-monegasy-device",
      device.isMobile ? "mobile" : "desktop"
    );

    // Styles par défaut (Monegasy branding)
    const defaultStyle: Record<string, string> = {
      background: "linear-gradient(135deg, #1F8A65 0%, #005C42 100%)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      padding: "14px 28px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 14px rgba(31, 138, 101, 0.4)",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      minWidth: "200px",
    };

    Object.assign(button.style, defaultStyle, params.buttonStyle || {});

    // Hover effects
    button.addEventListener("mouseenter", () => {
      button.style.transform = "translateY(-2px) scale(1.02)";
      button.style.boxShadow = "0 6px 20px rgba(31, 138, 101, 0.5)";
    });

    button.addEventListener("mouseleave", () => {
      button.style.transform = "translateY(0) scale(1)";
      button.style.boxShadow = "0 4px 14px rgba(31, 138, 101, 0.4)";
    });

    // Résolution du mode effectif
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const legacyAction = (params as any).mobileAction as string | undefined;
    const effectiveMode = params.mode ?? (legacyAction === "redirect" ? "redirect" : "iframe");

    // Click handler avec logique intelligente
    button.addEventListener("click", async () => {
      if (device.isMobile && effectiveMode !== "redirect") {
        // Sur mobile: tenter d'ouvrir l'app d'abord
        const opened = await this.openMobileApp(payment.linkId, {
          fallbackToStore: false,
          onNotInstalled: () => {
            if (effectiveMode === "iframe") {
              // Afficher le modal de choix : "Continuer sur le web" ou "Télécharger l'app"
              this._showMobileFallbackModal(payment.linkId, {
                onContinueWeb: () => {
                  this.openIframeModal(payment.linkId, {
                    onSuccess: params.onSuccess,
                    onError: params.onError,
                    onCancel: params.onCancel,
                  });
                },
              });
            } else {
              this.redirectToPayment(payment.linkId);
            }
          },
        });

        if (!opened && params.onCancel) {
          params.onCancel();
        }
      } else if (effectiveMode === "iframe") {
        // Desktop + mode iframe : overlay iframe
        this.openIframeModal(payment.linkId, {
          onSuccess: params.onSuccess,
          onError: params.onError,
          onCancel: params.onCancel,
        });
      } else if (effectiveMode === "redirect") {
        this.redirectToPayment(payment.linkId);
      } else {
        // popup (fallback)
        const popup = this.openPaymentPopup(payment.linkId);

        if (popup) {
          const messageHandler = (event: MessageEvent) => {
            if (!this._isTrustedOrigin(event.origin)) return;
            const { type, payment: paymentData, error } = event.data || {};

            if (type === "monegasy-payment-success" && params.onSuccess) {
              params.onSuccess(paymentData);
              cleanup();
            } else if (type === "monegasy-payment-error" && params.onError) {
              params.onError(new Error(error?.message || "Payment failed"));
              cleanup();
            } else if (type === "monegasy-payment-cancel" && params.onCancel) {
              params.onCancel();
              cleanup();
            }
          };

          const cleanup = () => {
            if (popup && !popup.closed) popup.close();
            window.removeEventListener("message", messageHandler);
          };

          window.addEventListener("message", messageHandler);

          const checkPopupInterval = setInterval(() => {
            if (popup && popup.closed) {
              clearInterval(checkPopupInterval);
              window.removeEventListener("message", messageHandler);
              if (params.onCancel) params.onCancel();
            }
          }, 500);
        }
      }
    });

    // Ajouter au DOM
    let containerElement: Element | null;

    if (typeof container === "string") {
      containerElement = document.querySelector(container);
      if (!containerElement) {
        throw new MonegasyValidationError(`Container "${container}" not found`);
      }
    } else {
      containerElement = container;
    }

    containerElement.appendChild(button);

    return button;
  }

  /**
   * Ouvre le paiement dans un iframe modal superposé à la page du merchant.
   * L'utilisateur ne quitte jamais le site du merchant.
   * @param linkId - ID du lien de paiement
   * @param options - Callbacks onSuccess / onError / onCancel
   * @returns Fonction pour fermer le modal manuellement
   */
  openIframeModal(
    linkId: string,
    options: {
      onSuccess?: (payment: PaymentLink) => void;
      onError?: (error: Error) => void;
      onCancel?: () => void;
    } = {}
  ): () => void {
    if (!linkId) throw new MonegasyValidationError("Payment link ID is required");

    const url = `${this.webURL}/pay/${linkId}?embed=1`;
    const device = MonegasySDK.detectDevice();

    // ── Overlay ──────────────────────────────────────────────────────────────
    const overlay = document.createElement("div");
    overlay.id = "monegasy-overlay";
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      background: "rgba(0,0,0,0.55)",
      zIndex: "999999",
      display: "flex",
      alignItems: device.isMobile ? "flex-end" : "center",
      justifyContent: "center",
      backdropFilter: "blur(2px)",
      animation: "mgFadeIn 0.2s ease",
    });

    // ── Container ─────────────────────────────────────────────────────────────
    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "relative",
      width: device.isMobile ? "100%" : "480px",
      height: device.isMobile ? "92vh" : "680px",
      borderRadius: device.isMobile ? "20px 20px 0 0" : "20px",
      overflow: "hidden",
      background: "#fff",
      boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      animation: device.isMobile ? "mgSlideUp 0.3s ease" : "mgZoomIn 0.2s ease",
    });

    // ── Barre supérieure ──────────────────────────────────────────────────────
    const topBar = document.createElement("div");
    Object.assign(topBar.style, {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      background: "#fff",
      borderBottom: "1px solid #F0F0F0",
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      zIndex: "1",
    });

    const brand = document.createElement("div");
    Object.assign(brand.style, {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "15px",
      fontWeight: "600",
      color: "#1F8A65",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    });
    brand.innerHTML = `<svg width="28" height="28" viewBox="125 385 275 250" xmlns="http://www.w3.org/2000/svg"><path fill="#005C42" d="M379.287781,537.000000 C379.277679,550.163635 379.364319,562.828308 379.238007,575.490784 C378.979919,601.359863 358.101196,622.056702 332.220337,622.060242 C284.565033,622.066895 236.909698,621.950256 189.254395,621.964539 C175.145935,621.968750 162.593033,618.133423 152.702484,607.639587 C144.263260,598.685547 139.616318,588.076538 139.618851,575.623108 C139.627228,534.466125 139.612411,493.309143 139.658508,452.152222 C139.688934,424.992706 159.465759,404.237000 186.558426,404.035980 C235.044266,403.676239 283.536499,403.687958 332.022522,404.036194 C359.286987,404.231964 379.226410,425.145874 379.268463,452.520233 C379.311462,480.513428 379.284485,508.506744 379.287781,537.000000 M174.897690,596.698730 C178.849304,598.585999 182.940735,599.719666 187.391968,599.715210 C235.380127,599.666748 283.368408,599.712280 331.356537,599.646790 C345.940460,599.626892 356.578522,589.026794 356.770660,574.458557 C356.913483,563.629456 357.009399,552.795288 356.823700,541.968506 C356.752777,537.834595 357.813446,534.982971 361.209229,532.212646 C369.724792,525.265564 370.311951,511.688660 362.017456,505.594086 C357.736084,502.448273 357.112793,498.948395 357.090790,494.585846 C357.022766,481.089478 357.001984,467.592834 356.948914,454.096375 C356.877594,435.954559 347.286346,426.250580 329.254242,426.241638 C282.432434,426.218445 235.610596,426.234497 188.788818,426.273987 C172.481537,426.287750 161.550858,436.543732 161.446014,452.921387 C161.190033,492.909790 161.245193,532.901672 161.464966,572.890564 C161.518921,582.706177 165.294113,591.083801 174.897690,596.698730 z"/><path fill="#005C42" d="M291.345520,545.788269 C307.450378,550.317139 323.761719,542.881104 326.206177,529.359680 C319.831635,529.359680 313.583130,529.542236 307.349579,529.314697 C299.055145,529.012085 294.003998,520.915100 297.144684,513.251831 C298.500366,509.943939 300.996338,507.208527 304.421967,507.063477 C313.826996,506.665192 323.259979,506.926056 332.491699,506.926056 C333.409760,509.291595 332.023254,510.593231 331.463257,512.090820 C327.774139,521.957458 333.245605,533.710754 343.418488,536.518921 C347.943329,537.767944 347.506439,539.755737 346.084717,542.970032 C336.664734,564.266968 312.090088,575.915039 288.580231,569.860718 C270.316254,565.157410 257.378265,554.016113 249.965408,536.589844 C249.325912,535.086487 248.679428,533.586121 247.550674,530.950745 C231.295975,552.392883 215.541504,573.175171 199.787018,593.957397 C199.274139,593.576538 198.761261,593.195679 198.248383,592.814819 C233.363251,537.601440 272.397003,485.159180 312.238373,433.248535 C308.366455,441.990662 302.069763,449.292419 297.546021,458.541412 C307.143219,458.408112 315.802460,460.336243 324.128479,463.754456 C329.383331,465.911804 332.583221,469.469635 332.407074,475.508545 C332.187073,483.050781 325.893372,488.151123 318.480530,486.640747 C317.346771,486.409729 316.171906,486.129089 315.157654,485.599976 C298.314819,476.814301 277.926605,486.793457 271.798065,502.850494 C265.117096,520.354919 272.926727,538.199646 291.345520,545.788269 z"/><path fill="#005C42" d="M199.316956,519.000122 C199.315796,531.496704 199.348297,543.493347 199.300354,555.489746 C199.269821,563.125854 194.098038,568.919373 187.467514,568.898315 C180.588577,568.876465 175.687683,563.400024 175.679214,555.535706 C175.656433,534.375122 175.633636,513.214233 175.729095,492.053986 C175.791412,478.242310 180.661804,466.736176 193.476135,460.273743 C206.003235,453.956177 218.284927,455.361908 229.706650,463.618225 C230.098099,463.901184 230.577072,464.063080 231.344528,464.445465 C236.535782,460.379730 242.276291,457.309753 249.141785,456.477936 C258.675659,455.322845 266.854309,458.209320 274.300140,463.867676 C276.794739,465.763428 277.578033,467.677612 275.280457,470.528992 C271.243195,475.539459 267.536041,480.815857 263.801361,485.832123 C262.930298,485.536926 262.497986,485.528351 262.330750,485.313477 C258.799652,480.776337 254.321198,478.511383 248.785400,480.911316 C243.194901,483.334930 241.902634,488.698700 242.544205,493.966522 C244.381363,509.050964 241.005569,522.204773 229.912430,533.020142 C228.741409,534.161743 228.014053,535.749268 227.025879,537.089355 C224.148468,540.991455 222.110336,540.736084 220.103775,536.160950 C219.016281,533.681335 218.805038,531.026794 218.810791,528.331970 C218.837128,516.002258 218.868149,503.672180 218.798676,491.342773 C218.758377,484.189423 215.494171,480.453979 209.419846,480.302582 C203.518295,480.155487 199.447540,484.565186 199.362289,491.507812 C199.251816,500.504059 199.323380,509.502533 199.316956,519.000122 z"/><path fill="#005C42" d="M361.255249,521.744263 C359.627594,527.819336 355.564301,530.633484 350.014832,531.301270 C344.769714,531.932312 341.093109,529.203003 338.580322,524.841980 C335.792938,520.004150 336.264313,515.288696 339.554962,510.836914 C343.875153,504.992188 350.108887,504.440338 356.762909,509.605164 C353.128265,511.512115 348.740692,513.917847 352.098846,518.354431 C354.993469,522.178711 357.816315,518.303955 360.216125,516.114807 C361.975647,517.731628 361.114838,519.643799 361.255249,521.744263 z"/></svg> Monegasy`;

    const closeBtn = document.createElement("button");
    Object.assign(closeBtn.style, {
      background: "#F5F5F5",
      border: "none",
      borderRadius: "50%",
      width: "32px",
      height: "32px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      color: "#666",
    });
    closeBtn.innerHTML = "×";
    closeBtn.setAttribute("aria-label", "Fermer");

    topBar.appendChild(brand);
    topBar.appendChild(closeBtn);

    // ── Iframe ────────────────────────────────────────────────────────────────
    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.setAttribute("allowpaymentrequest", "true");
    iframe.setAttribute("allow", "payment");
    Object.assign(iframe.style, {
      position: "absolute",
      top: "49px",
      left: "0",
      width: "100%",
      height: "calc(100% - 49px)",
      border: "none",
    });

    // ── CSS animations ────────────────────────────────────────────────────────
    if (!document.getElementById("monegasy-sdk-styles")) {
      const style = document.createElement("style");
      style.id = "monegasy-sdk-styles";
      style.textContent = `
        @keyframes mgFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes mgSlideUp { from { transform:translateY(100%) } to { transform:translateY(0) } }
        @keyframes mgZoomIn { from { opacity:0; transform:scale(0.95) } to { opacity:1; transform:scale(1) } }
      `;
      document.head.appendChild(style);
    }

    // ── Assembly ──────────────────────────────────────────────────────────────
    container.appendChild(topBar);
    container.appendChild(iframe);
    overlay.appendChild(container);
    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const close = () => {
      overlay.remove();
      document.body.style.overflow = "";
      window.removeEventListener("message", messageHandler);
      if (options.onCancel) options.onCancel();
    };

    // ── Messages postMessage depuis l'iframe ──────────────────────────────────
    const messageHandler = (event: MessageEvent) => {
      if (!this._isTrustedOrigin(event.origin)) return;
      const { type, payment, error } = event.data || {};
      if (type === "monegasy-payment-success") {
        overlay.remove();
        document.body.style.overflow = "";
        window.removeEventListener("message", messageHandler);
        if (options.onSuccess) options.onSuccess(payment);
      } else if (type === "monegasy-payment-error") {
        overlay.remove();
        document.body.style.overflow = "";
        window.removeEventListener("message", messageHandler);
        if (options.onError) options.onError(new Error(error?.message || "Payment failed"));
      } else if (type === "monegasy-payment-cancel") {
        close();
      }
    };

    closeBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    window.addEventListener("message", messageHandler);

    return close;
  }

  /**
   * Affiche un modal de choix sur mobile quand l'app n'est pas installée.
   * Propose : Ouvrir l'app (deep link retry) / Continuer sur le web / Télécharger l'app
   * @internal
   */
  private _showMobileFallbackModal(
    linkId: string,
    options: {
      onContinueWeb?: () => void;
      onDownloadApp?: () => void;
      onRetryApp?: () => void;
    } = {}
  ): void {
    const device = MonegasySDK.detectDevice();

    if (!document.getElementById("monegasy-sdk-styles")) {
      const style = document.createElement("style");
      style.id = "monegasy-sdk-styles";
      style.textContent = `
        @keyframes mgFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes mgSlideUp { from { transform:translateY(100%) } to { transform:translateY(0) } }
        @keyframes mgZoomIn { from { opacity:0; transform:scale(0.95) } to { opacity:1; transform:scale(1) } }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed", inset: "0", background: "rgba(0,0,0,0.5)",
      zIndex: "999999", display: "flex", alignItems: "flex-end",
      justifyContent: "center", backdropFilter: "blur(2px)",
    });

    const sheet = document.createElement("div");
    Object.assign(sheet.style, {
      width: "100%", background: "#fff", borderRadius: "20px 20px 0 0",
      padding: "24px 20px 36px", fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      animation: "mgSlideUp 0.3s ease",
    });

    sheet.innerHTML = `
      <div style="width:36px;height:4px;background:#E0E0E0;border-radius:2px;margin:0 auto 20px"></div>
      <div style="font-size:17px;font-weight:700;color:#111;margin-bottom:6px">Payer avec Monegasy</div>
      <div style="font-size:14px;color:#666;margin-bottom:24px">Comment souhaitez-vous continuer ?</div>
      <button id="mg-btn-web" style="width:100%;padding:15px;background:#1F8A65;color:#fff;border:none;border-radius:14px;font-size:16px;font-weight:600;cursor:pointer;margin-bottom:12px">
        Continuer sur le web
      </button>
      <button id="mg-btn-app" style="width:100%;padding:15px;background:#F5F5F5;color:#1F8A65;border:none;border-radius:14px;font-size:16px;font-weight:600;cursor:pointer;margin-bottom:12px">
        Télécharger l'app Monegasy
      </button>
      <button id="mg-btn-cancel" style="width:100%;padding:12px;background:transparent;color:#999;border:none;font-size:14px;cursor:pointer">
        Annuler
      </button>
    `;

    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const close = () => { overlay.remove(); document.body.style.overflow = ""; };

    sheet.querySelector("#mg-btn-web")!.addEventListener("click", () => {
      close();
      if (options.onContinueWeb) options.onContinueWeb();
    });

    sheet.querySelector("#mg-btn-app")!.addEventListener("click", () => {
      close();
      window.location.href = device.storeUrl;
      if (options.onDownloadApp) options.onDownloadApp();
    });

    sheet.querySelector("#mg-btn-cancel")!.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
  }
}

// Export nommé de la classe (pour import { MonegasySDK } from "@monegasy/sdk")
export { MonegasySDK };

// Codec QR EMV / BR Code (génération + parsing au standard EMVCo MPM)
export {
  buildMonegasyEmvQr,
  parseEmvQr,
  isEmvQr,
  crc16ccitt,
  MONEGASY_GUID,
  CURRENCY_MGA,
} from "./emv";
export type { EmvQrParams, ParsedEmvQr } from "./emv";

// Export pour compatibilité navigateur (UMD)
if (typeof window !== "undefined") {
  (window as any).MonegasySDK = MonegasySDK;
  (window as any).MonegasySDK.version = SDK_VERSION;
  (window as any).MonegasySDK.detectDevice = MonegasySDK.detectDevice;
}
